import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Plus, Minus } from "lucide-react";

const faqs = [
  {
    question: "How quickly can you deploy agents?",
    answer:
      "Our standard deployment timeline is 14 business days from contract signing. This includes recruitment, background verification, training on your product and processes, and system integration. For urgent needs, we offer expedited deployment in as little as 3-5 days with our Enterprise package.",
  },
  {
    question: "What support channels do you cover?",
    answer:
      "Our Growth package covers email, live chat, and WhatsApp support. Our Enterprise package adds phone support (inbound and outbound) and video call capabilities. We integrate with all major helpdesk platforms including Zendesk, Freshdesk, Intercom, and others.",
  },
  {
    question: "Are there long-term contracts?",
    answer:
      "No! Our Growth package is completely month-to-month with no long-term commitment required. Our Enterprise package requires a 3-month minimum commitment to ensure proper team stability and training investment, but still offers flexibility compared to traditional hiring.",
  },
  {
    question: "How do you ensure quality?",
    answer:
      "Quality is our top priority. Every agent undergoes rigorous training on your product and brand voice. We implement real-time monitoring, regular QA audits, and continuous coaching. You'll receive weekly performance reports with metrics like response time, resolution rate, and customer satisfaction scores.",
  },
  {
    question: "Can agents handle complex technical issues?",
    answer:
      "Absolutely. We carefully match agent skills to your requirements. For technical products, we recruit agents with relevant backgrounds and provide comprehensive technical training. Our Enterprise package includes escalation procedures and dedicated team leads who can handle the most complex cases.",
  },
  {
    question: "What timezone coverage do you provide?",
    answer:
      "We offer flexible timezone coverage based on your needs. Our agents are located across multiple time zones, allowing us to provide 24/7 support if required. During onboarding, we'll work with you to determine the optimal coverage schedule for your customer base.",
  },
  {
    question: "How does scaling work?",
    answer:
      "Scaling is seamless with Pragma. Need more agents during peak season? We can add trained agents within 1-2 weeks. Business slowing down? Scale back with just a week's notice on our Growth plan. You only pay for the hours worked—no idle time charges.",
  },
  {
    question: "What's included in the hourly rate?",
    answer:
      "Everything. Our rate includes agent salaries, benefits, payroll taxes, HR management, training, equipment, software licenses, quality monitoring, and management oversight. There are no hidden fees or surprise charges—the rate you see is the rate you pay.",
  },
];

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="py-24 relative overflow-hidden">
      {/* Background */}
      <div className="absolute top-0 right-0 w-96 h-96 border border-cyan-400/5 rounded-full translate-x-1/2 -translate-y-1/2" />

      <div className="container relative z-10">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16 max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <motion.span
            className="inline-block px-4 py-1.5 bg-cyan-400/10 border border-cyan-400/30 text-cyan-400 text-sm font-mono rounded-full mb-6"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            viewport={{ once: true }}
          >
            FAQ
          </motion.span>
          <h2 className="text-foreground mb-6">
            Frequently Asked <span className="text-cyan-400">Questions</span>
          </h2>
          <p className="text-gray-400 text-lg">
            Everything you need to know about working with Pragma.
          </p>
        </motion.div>

        {/* FAQ Items */}
        <div className="max-w-3xl mx-auto space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              viewport={{ once: true }}
              data-testid={`faq-item-${index}`}
            >
              <button
                onClick={() => toggleFAQ(index)}
                className={`w-full p-6 border transition-all duration-300 text-left flex items-center justify-between gap-4 ${
                  openIndex === index
                    ? "border-cyan-400 bg-cyan-400/5"
                    : "border-border bg-card/30 hover:border-border/80"
                }`}
              >
                <span
                  className={`font-mono font-bold text-lg transition-colors ${
                    openIndex === index ? "text-cyan-400" : "text-foreground"
                  }`}
                >
                  {faq.question}
                </span>
                <motion.span
                  animate={{ rotate: openIndex === index ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                  className={`flex-shrink-0 ${
                    openIndex === index ? "text-cyan-400" : "text-gray-500"
                  }`}
                >
                  {openIndex === index ? (
                    <Minus className="w-5 h-5" />
                  ) : (
                    <Plus className="w-5 h-5" />
                  )}
                </motion.span>
              </button>

              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="p-6 border border-t-0 border-cyan-400/30 bg-card/20">
                      <p className="text-muted-foreground leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
